import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { TrendingUp, Users, Wallet, Sparkles, ArrowRight } from 'lucide-react'
import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { Breadcrumb } from '@/components/Breadcrumb'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { toAbsoluteUrl } from '@/lib/seo'

const JOIN_URL = 'https://www.refindie.com/register/201/feedbask'
const RECURRING_COMMISSION_RATE = 0.3
const ILLUSTRATIVE_AVG_SALE_USD = 40
const ILLUSTRATIVE_COMMISSION_PER_SALE_USD =
	RECURRING_COMMISSION_RATE * ILLUSTRATIVE_AVG_SALE_USD

const benefits = [
	{
		icon: TrendingUp,
		title: 'Recurring revenue',
		body: 'Earn 30% recurring commission on every payment from customers you refer—for as long as they stay subscribed.',
	},
	{
		icon: Users,
		title: 'Built for your audience',
		body: 'Perfect if you cover SaaS, customer support, product management, or growth—your readers already need better feedback loops.',
	},
	{
		icon: Wallet,
		title: 'Straightforward payouts',
		body: 'Track performance in your affiliate dashboard and get paid reliably once you reach the minimum threshold.',
	},
	{
		icon: Sparkles,
		title: 'A product that sells itself',
		body: 'Feedbask bundles widgets, NPS, roadmap, and more—clear value for teams who live in their product every day.',
	},
]

const steps = [
	{
		title: 'Apply in minutes',
		body: 'Sign up through our partner platform with your details. No Feedbask subscription required to promote us.',
	},
	{
		title: 'Share your link',
		body: 'Drop your tracking link in blog posts, newsletters, videos, or social—plus ready-made assets when you need them.',
	},
	{
		title: 'Earn on every renewal',
		body: 'Recurring commissions mean you keep getting paid every billing cycle while your referrals remain active.',
	},
]

const faqs = [
	{
		q: 'How much can I earn?',
		a: 'You earn 30% recurring commission on each qualifying sale (for example, a $40 average sale means about $12 commission per sale). That recurring rate applies on each payment for as long as they remain customers. Full terms appear in your affiliate dashboard after you join.',
	},
	{
		q: 'When do I get paid?',
		a: 'Payouts are processed on a regular schedule through our affiliate partner once you pass the minimum balance.',
	},
	{
		q: 'How long is the referral window?',
		a: 'Attribution uses a cookie window defined in the program terms so you get credit when someone signs up after clicking your link.',
	},
	{
		q: 'Can I promote Feedbask anywhere?',
		a: 'Yes—blogs, YouTube, LinkedIn, email, and more. Always disclose your affiliate relationship clearly, as required by law and platform rules.',
	},
]

const AffiliateProgramPage = () => {
	const [referrals, setReferrals] = useState([50])

	const { monthly, yearly } = useMemo(() => {
		const n = referrals[0] ?? 1
		const m = n * ILLUSTRATIVE_COMMISSION_PER_SALE_USD
		return { monthly: m, yearly: m * 12 }
	}, [referrals])

	const fmt = useMemo(
		() =>
			new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: 0,
			}),
		[],
	)

	const breadcrumbs = [
		{ name: 'Home', url: '/' },
		{ name: 'Affiliate program', url: '/affiliate-program' },
	]

	const breadcrumbSchema = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: breadcrumbs.map((c, i) => ({
			'@type': 'ListItem',
			position: i + 1,
			name: c.name,
			item: toAbsoluteUrl(c.url),
		})),
	}

	const title = 'Affiliate Program | Feedbask'
	const description =
		'Earn 30% recurring commission promoting Feedbask. Join our affiliate program and turn your audience into long-term passive income.'

	return (
		<div className="min-h-screen w-full mx-auto relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
			<Helmet>
				<title>{title}</title>
				<meta name="description" content={description} />
				<link rel="canonical" href={toAbsoluteUrl('/affiliate-program')} />
				<meta property="og:title" content={title} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={toAbsoluteUrl('/affiliate-program')} />
				<meta property="og:type" content="website" />
				<script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
			</Helmet>

			<div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10">
				<div className="absolute top-24 left-10 w-64 h-64 rounded-full bg-[#866BFF] opacity-25 blur-3xl" />
				<div className="absolute bottom-32 right-10 w-72 h-72 rounded-full bg-indigo-200 opacity-30 blur-3xl" />
			</div>

			<div className="relative z-10 max-w-7xl px-4 mx-auto">
				<Header />
				<Breadcrumb items={breadcrumbs} />

				<motion.section
					initial={{ opacity: 0, y: 24 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="py-12 md:py-20 text-center mx-auto"
				>
					<p className="text-sm font-semibold uppercase tracking-wide text-[#866BFF] mb-3">
						30% recurring commission · Feedbask affiliates
					</p>
					<h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
						Turn your audience into recurring income
					</h1>
					<p className="text-lg text-gray-600 mb-8">
						Promote Feedbask and earn <strong className="text-gray-800">30% recurring</strong>{' '}
						commission on every subscription payment from your referrals, month after month.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Button
							asChild
							size="lg"
							className="bg-[#866BFF] hover:bg-[#755ce0] text-white rounded-full px-8"
						>
							<a href={JOIN_URL} target="_blank" rel="noopener noreferrer">
								Join now
								<ArrowRight className="ml-2 h-4 w-4" />
							</a>
						</Button>
						<Button asChild variant="outline" size="lg" className="rounded-full px-8 border-gray-300">
							<Link to="/#pricing">See Feedbask pricing</Link>
						</Button>
					</div>
				</motion.section>

				<section className="pb-16 md:pb-24">
					<div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 md:p-10 max-w-2xl mx-auto">
						<h2 className="text-xl font-bold text-gray-900 text-center mb-2">Estimate your recurring earnings</h2>
						<p className="text-sm text-gray-500 text-center mb-8">
							Illustrative math: <strong className="text-gray-700">30% recurring</strong> commission on each qualifying sale.
							<br /> Average sale{' '}
							{fmt.format(ILLUSTRATIVE_AVG_SALE_USD)} → you earn {fmt.format(ILLUSTRATIVE_COMMISSION_PER_SALE_USD)} per sale .
						</p>
						<div className="mb-6">
							<div className="flex justify-between items-baseline mb-3">
								<span className="text-sm font-medium text-gray-700">Number of referrals</span>
								<span className="text-2xl font-bold text-[#866BFF]">{referrals[0]}</span>
							</div>
							<Slider
								value={referrals}
								onValueChange={setReferrals}
								min={1}
								max={150}
								step={1}
								className="w-full"
							/>
							<div className="flex justify-between text-xs text-gray-400 mt-2">
								<span>1</span>
								<span>150</span>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="rounded-xl bg-gradient-to-br from-[#866BFF]/10 to-indigo-50 p-5 text-center border border-[#866BFF]/20">
								<p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Recurring monthly (illustrative)</p>
								<p className="text-2xl md:text-3xl font-bold text-gray-900">{fmt.format(monthly)}</p>
							</div>
							<div className="rounded-xl bg-gray-50 p-5 text-center border border-gray-200">
								<p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Recurring yearly (illustrative)</p>
								<p className="text-2xl md:text-3xl font-bold text-gray-900">{fmt.format(yearly)}</p>
							</div>
						</div>
						<div className="mt-8 text-center">
							<Button asChild className="bg-[#866BFF] hover:bg-[#755ce0] text-white rounded-full px-8">
								<a href={JOIN_URL} target="_blank" rel="noopener noreferrer">
									Join now
									<ArrowRight className="ml-2 h-4 w-4" />
								</a>
							</Button>
						</div>
					</div>
				</section>

				<section className="pb-16 md:pb-24">
					<h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">Why promote Feedbask (30% recurring)</h2>
					<div className="grid md:grid-cols-2 gap-6">
						{benefits.map(({ icon: Icon, title, body }) => (
							<motion.div
								key={title}
								initial={{ opacity: 0, y: 16 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ duration: 0.4 }}
								className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
							>
								<div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#866BFF]/15 text-[#866BFF] mb-4">
									<Icon className="h-5 w-5" />
								</div>
								<h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
								<p className="text-gray-600 text-sm leading-relaxed">{body}</p>
							</motion.div>
						))}
					</div>
				</section>

				<section className="pb-16 md:pb-24">
					<h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-4">How it works</h2>
					<p className="text-center text-gray-600 max-w-2xl mx-auto mb-10">
						Three simple steps from signup to <strong className="text-gray-800">recurring</strong> payouts—managed through our affiliate partner
						platform.
					</p>
					<div className="grid md:grid-cols-3 gap-8">
						{steps.map((s, i) => (
							<div key={s.title} className="text-center md:text-left">
								<div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#866BFF] text-white font-bold mb-4">
									{i + 1}
								</div>
								<h3 className="text-lg font-semibold text-gray-900 mb-2">{s.title}</h3>
								<p className="text-gray-600 text-sm leading-relaxed">{s.body}</p>
							</div>
						))}
					</div>
				</section>

				<section className="pb-20">
					<h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">Frequently asked questions</h2>
					<div className="max-w-3xl mx-auto space-y-4">
						{faqs.map((item) => (
							<div key={item.q} className="bg-white rounded-xl border border-gray-200 p-6">
								<h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
								<p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
							</div>
						))}
					</div>
					<div className="text-center mt-12">
						<p className="text-gray-600 mb-4">Ready to earn recurring commission with Feedbask?</p>
						<Button asChild size="lg" className="bg-[#866BFF] hover:bg-[#755ce0] text-white rounded-full px-10">
							<a href={JOIN_URL} target="_blank" rel="noopener noreferrer">
								Join now
								<ArrowRight className="ml-2 h-4 w-4" />
							</a>
						</Button>
					</div>
				</section>

				<Footer />
			</div>
		</div>
	)
}

export default AffiliateProgramPage
